import React, { useEffect, useMemo, useRef } from 'react';
import { CalendarDays, Megaphone } from 'lucide-react';

const dateAtOffset = (launchDate, offsetDays) => {
  const match = String(launchDate || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return '';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  date.setUTCDate(date.getUTCDate() + offsetDays);
  return date.toISOString().slice(0, 10);
};

export const buildMediaLaunchCalendar = (launchDate) => [
  { id: 'campaign-planning', offset: -28, channel: 'Planning', content: 'Confirm the campaign message, image rights, partner handle, URLs, and approval owners', partners: 'Client communications lead, venue team, GuideSofia', owner: '', status: 'Planned' },
  { id: 'press-outreach', offset: -21, channel: 'Press', content: 'Build the press list and offer the approved press release under embargo', partners: 'Local newspapers, cultural editors, radio producers, municipality communications', owner: '', status: 'Planned' },
  { id: 'website-ready', offset: -14, channel: 'Website', content: 'Prepare and approve the website announcement', partners: 'Client website and institutional communications team', owner: '', status: 'Planned' },
  { id: 'partner-preview', offset: -10, channel: 'Partner outreach', content: 'Send preview copy and the visitor link to amplification partners', partners: 'Tourism board, visitor centre, hotels, tour guides, cultural associations', owner: '', status: 'Planned' },
  { id: 'social-teaser', offset: -7, channel: 'Instagram / Facebook', content: 'Publish the launch teaser or carousel', partners: 'Approved client and destination accounts to mention', owner: '', status: 'Planned' },
  { id: 'newsletter', offset: -3, channel: 'Newsletter', content: 'Send the launch email to subscribers and members', partners: 'Client mailing list and member organisations', owner: '', status: 'Planned' },
  { id: 'launch-reminder', offset: -1, channel: 'Instagram / Facebook Stories', content: 'Publish a short launch reminder or countdown', partners: 'Partner account and @guide.sofia', owner: '', status: 'Planned' },
  { id: 'launch-day', offset: 0, channel: 'All launch channels', content: 'Publish the main announcement, website item, and first Story', partners: 'Partner account, @guide.sofia, municipality, tourism and venue partners', owner: '', status: 'Planned' },
  { id: 'story-series', offset: 3, channel: 'Instagram / Facebook Stories', content: 'Publish the second independent Story and direct visitors to the audio guide', partners: 'Mention only approved accounts relevant to this Story', owner: '', status: 'Planned' },
  { id: 'partner-reshare', offset: 5, channel: 'Partner outreach', content: 'Send a ready-to-share Story and caption to priority partners', partners: 'Tourism board, visitor centre, hotels, guides, nearby cultural venues', owner: '', status: 'Planned' },
  { id: 'partner-followup', offset: 7, channel: 'Partner follow-up', content: 'Follow up with partners and provide ready-to-share assets', partners: 'Partners who have not yet shared the launch', owner: '', status: 'Planned' },
  { id: 'story-three', offset: 14, channel: 'Instagram / Facebook Stories', content: 'Publish another strong guide Story and answer a practical visitor question', partners: 'Partner account, @guide.sofia, relevant local cultural accounts', owner: '', status: 'Planned' },
  { id: 'evergreen', offset: 30, channel: 'Social / Website', content: 'Publish an evergreen Story, review performance, and refresh the best-performing assets', partners: 'Strongest amplifying partners and relevant local networks', owner: '', status: 'Planned' },
].map((item) => ({ ...item, date: dateAtOffset(launchDate, item.offset) }));

const formatDate = (value, language) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value || '';
  return new Intl.DateTimeFormat(language || 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))));
};

const phaseForItem = (item, launchDate) => {
  if (Number.isFinite(item.offset)) return item.offset < 0 ? 'before' : item.offset > 0 ? 'after' : 'launch';
  if (!item.date || !launchDate) return 'launch';
  return item.date < launchDate ? 'before' : item.date > launchDate ? 'after' : 'launch';
};

export const MediaLaunchTimeline = ({
  items,
  launchDate,
  language = 'en-GB',
  partnerHandle = '',
  compact = false,
  completionById = null,
  updatingId = '',
  onToggle,
}) => {
  const isPortuguese = String(language).toLowerCase().startsWith('pt');
  const timelineItems = Array.isArray(items) && items.length > 0 ? items : buildMediaLaunchCalendar(launchDate);
  const phases = [
    { id: 'before', label: isPortuguese ? 'Antes do lançamento' : 'Before launch', tone: 'border-sky-200 bg-sky-50', dot: 'bg-sky-500' },
    { id: 'launch', label: isPortuguese ? 'Dia do lançamento' : 'Launch day', tone: 'border-fuchsia-200 bg-fuchsia-50', dot: 'bg-fuchsia-500' },
    { id: 'after', label: isPortuguese ? 'Depois do lançamento' : 'After launch', tone: 'border-emerald-200 bg-emerald-50', dot: 'bg-emerald-500' },
  ];
  const completedCount = timelineItems.filter((item) => Boolean(completionById?.[item.id])).length;
  const progressPercent = timelineItems.length ? Math.round((completedCount / timelineItems.length) * 100) : 0;

  return (
    <section className={`${compact ? '' : 'rounded-2xl border border-indigo-200 bg-indigo-50/40 p-5 shadow-sm'}`}>
      {!compact && (
        <div>
          <div className="flex items-center gap-2"><CalendarDays className="h-5 w-5 text-indigo-600" /><h3 className="text-lg font-semibold text-slate-900">{isPortuguese ? 'Calendário de lançamento e parceiros' : 'Launch and partner calendar'}</h3></div>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">{isPortuguese ? 'Um plano automático, calculado a partir da data de lançamento. Consulte a sequência recomendada antes, durante e depois do lançamento.' : 'An automatic plan calculated from the launch date. Consult the recommended sequence before, during, and after launch.'}</p>
          {partnerHandle && <p className="mt-2 text-xs font-semibold text-indigo-800">@guide.sofia · {partnerHandle}</p>}
        </div>
      )}

      {!launchDate ? (
        <div className={`${compact ? '' : 'mt-4'} rounded-xl border border-dashed border-indigo-300 bg-white p-4 text-sm text-slate-600`}>{isPortuguese ? 'Defina a data de lançamento para gerar o calendário automaticamente.' : 'Set the launch date to generate the calendar automatically.'}</div>
      ) : (
        <>
        {completionById && (
          <div className={`${compact ? '' : 'mt-5'} rounded-xl border border-slate-200 bg-white p-4`}>
            <div className="flex items-center justify-between gap-3 text-sm"><span className="font-semibold text-slate-900">{isPortuguese ? 'Progresso do lançamento' : 'Launch progress'}</span><span className="font-medium text-slate-600">{completedCount}/{timelineItems.length} · {progressPercent}%</span></div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full bg-cyan-600 transition-all" style={{ width: `${progressPercent}%` }} /></div>
            <p className="mt-2 text-xs text-slate-500">{isPortuguese ? 'As datas e recomendações são fixas. Assinale apenas as ações concluídas.' : 'Dates and recommendations are fixed. Check off only completed actions.'}</p>
          </div>
        )}
        <div className={`${compact || completionById ? 'mt-4' : 'mt-5'} grid items-start gap-4 lg:grid-cols-3`}>
          {phases.map((phase) => {
            const phaseItems = timelineItems.filter((item) => phaseForItem(item, launchDate) === phase.id);
            return (
              <div key={phase.id} className={`rounded-2xl border p-4 ${phase.tone}`}>
                <div className="flex items-center justify-between gap-3"><h4 className="text-sm font-semibold text-slate-900">{phase.label}</h4><span className="rounded-full bg-white/80 px-2 py-1 text-xs font-medium text-slate-600">{phaseItems.length}</span></div>
                <div className="relative mt-4 space-y-3 before:absolute before:bottom-3 before:left-[7px] before:top-3 before:w-px before:bg-slate-300">
                  {phaseItems.map((item) => (
                    <article key={item.id} className="relative pl-6">
                      <span className={`absolute left-0 top-2 h-[15px] w-[15px] rounded-full border-2 border-white shadow-sm ${phase.dot}`} />
                      <div className="rounded-xl border border-white/80 bg-white p-3 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{formatDate(item.date, language)}</span><span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-600">{item.channel}</span></div>
                        <p className="mt-2 text-sm font-medium leading-5 text-slate-900">{item.content}</p>
                        {item.partners && <div className="mt-2 flex gap-2 text-xs leading-5 text-slate-600"><Megaphone className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>{item.partners}</span></div>}
                        {completionById && (
                          <label className="mt-3 flex cursor-pointer items-center gap-2 border-t border-slate-100 pt-3 text-xs font-semibold text-slate-700">
                            <input type="checkbox" checked={Boolean(completionById[item.id])} disabled={!onToggle || updatingId === item.id} onChange={(event) => onToggle?.(item.id, event.target.checked)} className="h-4 w-4 rounded border-slate-300 accent-cyan-700" />
                            {isPortuguese ? 'Concluído' : 'Completed'}
                          </label>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}
    </section>
  );
};

const MediaLaunchCalendar = ({ launchDate, partnerHandle, items, onChange, compact = false }) => {
  const calendarItems = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const previousLaunchDateRef = useRef(launchDate);

  useEffect(() => {
    if (!launchDate) {
      previousLaunchDateRef.current = launchDate;
      return;
    }
    if (calendarItems.length === 0) {
      onChange(buildMediaLaunchCalendar(launchDate));
    } else if (previousLaunchDateRef.current && previousLaunchDateRef.current !== launchDate) {
      onChange(calendarItems.map((item) => ({
        ...item,
        date: Number.isFinite(item.offset) ? dateAtOffset(launchDate, item.offset) : item.date,
      })));
    }
    previousLaunchDateRef.current = launchDate;
  }, [calendarItems, launchDate, onChange]);

  return <MediaLaunchTimeline compact={compact} items={calendarItems} launchDate={launchDate} partnerHandle={partnerHandle} />;
};

export default MediaLaunchCalendar;
